// Copyright (c) 2021 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import * as React from "react";
import { connect } from "react-redux";
import { FormattedMessage, defineMessages, WrappedComponentProps, injectIntl } from "react-intl";
import { Button, Collapse, Form } from "react-bootstrap";
import InputNumber from "rc-input-number";
import { RoutedProps, withRouter } from "view/reactrouterinterceptor";

import { DeviceType, AlarmSource } from "dbtypes";
import { DataState } from "types";
import { isSet } from "utils";
import { IAction } from "actions";
import { IState } from "states/states";
import { AlarmSettings, Site } from "states/sitestates";

import { ReactComponent as IconExpandMore } from "static/iconExpandMore.svg";
import { ReactComponent as IconExpandLess } from "static/iconExpandLess.svg";

interface IAlarmSettingsExtProps {
  settings: AlarmSettings | undefined; // include this to ensure screen update
  onChange: (alarmSettings: AlarmSettings) => void;
}

interface IAlarmSettingsProps {
  site: Site | undefined;
  dataState: DataState;
}

const mapStateToProps = (state: IState, ownProps: IAlarmSettingsExtProps) => {
  const siteId = state.cached.selectedSite;
  const site = state.site.siteList?.find((curSite) => curSite.id === siteId);

  const props: IAlarmSettingsProps = {
    site,
    dataState: state.scheduler.saveState,
  };
  return props;
};

type IAlarmSettingsState = {
  isAlarmExpanded: boolean;
};

interface IAlarmSettingsDispatch {}

const mapDispatchToProps = (dispatch: (action: IAction<any>) => void) => {
  const dispatchProps: IAlarmSettingsDispatch = {};
  return dispatchProps;
};

const messages = defineMessages({});

class AlarmSettingsComponent extends React.Component<
  IAlarmSettingsExtProps &
    IAlarmSettingsProps &
    IAlarmSettingsDispatch &
    WrappedComponentProps &
    RoutedProps,
  IAlarmSettingsState
> {
  state: IAlarmSettingsState = {
    isAlarmExpanded: false,
  };

  componentDidMount() {
    if (this.props.settings) {
      this.setState({
        isAlarmExpanded: false,
      });
    }
  }

  private changeAlarmSettings(curAlarm: AlarmSource, newSetting: boolean) {
    const stateChange = {
      ...this.props.settings,
      emailEnabled: {
        ...this.props.settings?.emailEnabled,
      },
    };
    stateChange.emailEnabled[curAlarm] = newSetting;
    this.props.onChange(stateChange);
  }

  private getEmailRows() {
    const isEditMode = true;
    const emailItems = [
      AlarmSource.OcppChargerOffline,
      AlarmSource.OcppChargerFaulted,
      AlarmSource.OcppChargingInterrupted,
    ];
    if (this.props.site?.deviceType !== DeviceType.VIRTUAL) {
      emailItems.push(
        AlarmSource.Offline,
        AlarmSource.TargetValue,
        AlarmSource.Restart,
        AlarmSource.MissingSync,
        AlarmSource.EnergyMeter,
        AlarmSource.Modul,
        AlarmSource.ManualActive,
        AlarmSource.Linx,
        AlarmSource.TrendMonitor,
      );
    }
    const emailRows = emailItems.map((curAlarm) => {
      const isCurAlarmDisabled =
        (curAlarm === AlarmSource.Offline &&
          (!isSet(this.props.settings?.offlineTimeout) ||
            this.props.settings?.offlineTimeout === 0)) ||
        (curAlarm === AlarmSource.OcppChargerOffline &&
          (!isSet(this.props.settings?.chargerOfflineTimeout) ||
            this.props.settings?.chargerOfflineTimeout === 0)) ||
        (curAlarm === AlarmSource.OcppChargerFaulted &&
          (!isSet(this.props.settings?.chargerFaultedTimeout) ||
            this.props.settings?.chargerFaultedTimeout === 0)) ||
        (curAlarm === AlarmSource.OcppChargingInterrupted &&
          (!isSet(this.props.settings?.chargingInterruptedSoc) ||
            this.props.settings?.chargingInterruptedSoc === 0));
      return (
        <tr key={curAlarm} className={isCurAlarmDisabled ? "disabled" : ""}>
          <td>
            <Form.Label htmlFor={`emailEnabled_${curAlarm}`}>
              <FormattedMessage id="Enabled" />: <FormattedMessage id={`AlarmSource_${curAlarm}`} />
            </Form.Label>
          </td>
          <td>
            {isEditMode ? (
              <Form.Check
                id={`emailEnabled_${curAlarm}`}
                name={`emailEnabled_${curAlarm}`}
                disabled={isCurAlarmDisabled}
                checked={
                  (isSet(this.props.settings?.emailEnabled) &&
                    this.props.settings!.emailEnabled[curAlarm]) === true
                }
                onChange={(event) => {
                  this.changeAlarmSettings(curAlarm, event?.currentTarget.checked);
                }}
                className="ms-1"
              />
            ) : this.props.settings?.emailEnabled && this.props.settings?.emailEnabled[curAlarm] ? (
              <FormattedMessage id="yes" />
            ) : (
              <FormattedMessage id="no" />
            )}
          </td>
        </tr>
      );
    });
    return emailRows;
  }

  public render() {
    const isEditMode = true;
    const emailRows = this.getEmailRows();

    return (
      <>
        <h1
          className="link"
          onClick={() => {
            this.setState({ isAlarmExpanded: !this.state.isAlarmExpanded });
          }}
        >
          <Button variant="link" className="expand-button me-2 d-print-none">
            {this.state.isAlarmExpanded ? <IconExpandMore /> : <IconExpandLess />}
          </Button>
          <FormattedMessage id="Alarm Settings" />
        </h1>
        <Collapse in={this.state.isAlarmExpanded} className="fullwidthwhite">
          <div>
            <table>
              <thead>
                <tr className="mt-2">
                  <th colSpan={2}>
                    <FormattedMessage id="Activated Alarms" />
                  </th>
                </tr>
              </thead>
              <tbody>
                {this.renderChargerAlarms()}
                {this.props.site?.deviceType !== DeviceType.VIRTUAL && (
                  <>
                    <tr className="mt-2">
                      <td
                        rowSpan={this.props.settings?.offlineTimeout ? 2 : 1}
                        style={{ verticalAlign: "top" }}
                        onClick={(event) => {
                          this.props.onChange({
                            ...this.props.settings,
                            offlineTimeout:
                              !isSet(this.props.settings?.offlineTimeout) ||
                              this.props.settings?.offlineTimeout === 0
                                ? 3600
                                : 0,
                          });
                        }}
                      >
                        <FormattedMessage id={`AlarmSource_${AlarmSource.Offline}`} />
                      </td>
                      <td>
                        {isEditMode ? (
                          <Form.Check
                            name="offlineAlarmEnabled"
                            id="offlineAlarmEnabled"
                            checked={(this.props.settings?.offlineTimeout ?? 0) > 0}
                            onChange={(event) => {
                              this.props.onChange({
                                ...this.props.settings,
                                offlineTimeout: event?.currentTarget.checked ? 3600 : 0,
                              });
                            }}
                            label={<FormattedMessage id="Enabled" />}
                          />
                        ) : this.props.settings?.offlineTimeout ? (
                          <FormattedMessage id="enabled" />
                        ) : (
                          <FormattedMessage id="disabled" />
                        )}
                      </td>
                    </tr>
                    {(this.props.settings?.offlineTimeout ?? 0) > 0 && (
                      <tr>
                        <td>
                          <FormattedMessage id="Timeout" />:{" "}
                          {isEditMode ? (
                            <>
                              <InputNumber
                                type="number"
                                className="ms-1 default-border"
                                style={{
                                  marginTop: -3,
                                  display: "inline-block",
                                }}
                                value={
                                  this.props.settings?.offlineTimeout
                                    ? this.props.settings?.offlineTimeout / 3600
                                    : undefined
                                }
                                min={1}
                                disabled={this.props.dataState === DataState.SAVE_REQ}
                                onChange={(number) => {
                                  if (isSet(number)) {
                                    this.props.onChange({
                                      ...this.props.settings,
                                      offlineTimeout: number * 3600,
                                    });
                                  }
                                }}
                              />{" "}
                              <FormattedMessage id="hour(s)" />
                            </>
                          ) : (
                            this.props.settings?.offlineTimeout
                          )}
                        </td>
                      </tr>
                    )}
                  </>
                )}

                <tr className="mt-2">
                  <th colSpan={2}>
                    <FormattedMessage id="Email Notifications" />
                  </th>
                </tr>
                <tr>
                  <td>
                    <FormattedMessage id="Email Address" />
                  </td>
                  <td>
                    {isEditMode ? (
                      <input
                        type="text"
                        value={this.props.settings?.emailAddress ?? ""}
                        disabled={this.props.dataState === DataState.SAVE_REQ}
                        onChange={(event) => {
                          this.props.onChange({
                            ...this.props.settings,
                            emailAddress: event?.currentTarget.value.replace(";", ","),
                          });
                        }}
                      />
                    ) : (
                      this.props.settings?.emailAddress
                    )}
                  </td>
                </tr>
                {emailRows}
              </tbody>
            </table>
          </div>
        </Collapse>
      </>
    );
  }

  private renderChargerAlarms() {
    const isEditMode = true;
    return (
      <>
        <tr className="mt-3">
          <td
            rowSpan={this.props.settings?.chargerOfflineTimeout ? 2 : 1}
            style={{ verticalAlign: "top" }}
            onClick={(event) => {
              this.props.onChange({
                ...this.props.settings,
                chargerOfflineTimeout:
                  !isSet(this.props.settings?.chargerOfflineTimeout) ||
                  this.props.settings?.chargerOfflineTimeout === 0
                    ? 3600
                    : 0,
              });
            }}
          >
            <FormattedMessage id={`AlarmSource_${AlarmSource.OcppChargerOffline}`} />
          </td>
          <td>
            {isEditMode ? (
              <Form.Check
                name="chargerOfflineEnabled"
                id="chargerOfflineEnabled"
                checked={(this.props.settings?.chargerOfflineTimeout ?? 0) > 0}
                onChange={(event) => {
                  this.props.onChange({
                    ...this.props.settings,
                    chargerOfflineTimeout: event?.currentTarget.checked ? 3600 : 0,
                  });
                }}
                label={<FormattedMessage id="Enabled" />}
              />
            ) : this.props.settings?.chargerOfflineTimeout ? (
              <FormattedMessage id="enabled" />
            ) : (
              <FormattedMessage id="disabled" />
            )}
          </td>
        </tr>
        {(this.props.settings?.chargerOfflineTimeout ?? 0) > 0 && (
          <tr>
            <td>
              <FormattedMessage id="Timeout" />:{" "}
              {isEditMode ? (
                <>
                  <InputNumber
                    type="number"
                    className="ms-1 default-border"
                    value={
                      this.props.settings?.chargerOfflineTimeout
                        ? this.props.settings?.chargerOfflineTimeout / 3600
                        : undefined
                    }
                    min={1}
                    disabled={this.props.dataState === DataState.SAVE_REQ}
                    onChange={(number) => {
                      if (isSet(number)) {
                        this.props.onChange({
                          ...this.props.settings,
                          chargerOfflineTimeout: number * 3600,
                        });
                      }
                    }}
                  />{" "}
                  <FormattedMessage id="hour(s)" />
                </>
              ) : (
                this.props.settings?.chargerOfflineTimeout
              )}
            </td>
          </tr>
        )}

        <tr className="mt-2">
          <td
            style={{ verticalAlign: "top" }}
            onClick={(event) => {
              this.props.onChange({
                ...this.props.settings,
                chargerFaultedTimeout:
                  !isSet(this.props.settings?.chargerFaultedTimeout) ||
                  this.props.settings?.chargerFaultedTimeout === 0
                    ? 3600
                    : 0,
              });
            }}
          >
            <FormattedMessage id={`AlarmSource_${AlarmSource.OcppChargerFaulted}`} />
          </td>
          <td>
            {isEditMode ? (
              <Form.Check
                name="chargerFaultedEnabled"
                id="chargerFaultedEnabled"
                checked={(this.props.settings?.chargerFaultedTimeout ?? 0) > 0}
                onChange={(event) => {
                  this.props.onChange({
                    ...this.props.settings,
                    chargerFaultedTimeout: event?.currentTarget.checked ? 3600 : 0,
                  });
                }}
                label={<FormattedMessage id="Enabled" />}
              />
            ) : this.props.settings?.chargerFaultedTimeout ? (
              <FormattedMessage id="enabled" />
            ) : (
              <FormattedMessage id="disabled" />
            )}
          </td>
        </tr>

        <tr className="mt-2">
          <td
            rowSpan={(this.props.settings?.chargingInterruptedSoc ?? 0) > 0 ? 2 : 1}
            style={{ verticalAlign: "top" }}
            onClick={(event) => {
              this.props.onChange({
                ...this.props.settings,
                chargingInterruptedSoc:
                  !isSet(this.props.settings?.chargingInterruptedSoc) ||
                  this.props.settings?.chargingInterruptedSoc === 0
                    ? 80
                    : 0,
              });
            }}
          >
            <FormattedMessage id={`AlarmSource_${AlarmSource.OcppChargingInterrupted}`} />
          </td>
          <td>
            {isEditMode ? (
              <Form.Check
                name="chargingInterruptedEnabled"
                id="chargingInterruptedEnabled"
                checked={(this.props.settings?.chargingInterruptedSoc ?? 0) > 0}
                onChange={(event) => {
                  this.props.onChange({
                    ...this.props.settings,
                    chargingInterruptedSoc: event?.currentTarget.checked ? 80 : 0,
                  });
                }}
                label={<FormattedMessage id="Enabled" />}
              />
            ) : this.props.settings?.chargingInterruptedSoc ? (
              <>
                <FormattedMessage id="enabled" /> ({this.props.settings?.chargingInterruptedSoc} %)
              </>
            ) : (
              <FormattedMessage id="disabled" />
            )}
          </td>
        </tr>
        {(this.props.settings?.chargingInterruptedSoc ?? 0) > 0 && (
          <tr>
            <td>
              <FormattedMessage id="SoC" />:{" "}
              {isEditMode ? (
                <>
                  <InputNumber
                    type="number"
                    className="ms-1 default-border"
                    style={{
                      marginTop: -3,
                      display: "inline-block",
                    }}
                    value={this.props.settings?.chargingInterruptedSoc}
                    min={1}
                    max={99}
                    disabled={this.props.dataState === DataState.SAVE_REQ}
                    onChange={(number) => {
                      if (isSet(number)) {
                        this.props.onChange({
                          ...this.props.settings,
                          chargingInterruptedSoc: number,
                        });
                      }
                    }}
                  />{" "}
                  %
                </>
              ) : (
                <>{this.props.settings?.chargingInterruptedSoc} %</>
              )}
            </td>
          </tr>
        )}
      </>
    );
  }
}

export default injectIntl(
  connect(mapStateToProps, mapDispatchToProps)(withRouter(AlarmSettingsComponent)),
);
